import type { ClientConfigResponse } from '@ergo/shared';
import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Google Maps, loaded lazily and once. Everything here is best-effort: no key,
 * an offline device or a Google outage must leave the screen exactly as it was,
 * minus the map. Callers therefore get `null` rather than an exception.
 */

const SCRIPT_ID = 'google-maps-js';

let configPromise: Promise<ClientConfigResponse> | null = null;
let apiPromise: Promise<typeof google.maps | null> | null = null;

/** Cached for the tab: what the server is configured with changes on the server. */
export function clientConfig(): Promise<ClientConfigResponse> {
  configPromise ??= api.maps
    .config()
    .catch(() => ({ mapsBrowserKey: null, emailConfigured: false }));
  return configPromise;
}

/** After the SMTP settings change, so « Envoyer par email » stops trusting a stale answer. */
export function resetClientConfig(): void {
  configPromise = null;
}

export function mapsBrowserKey(): Promise<string | null> {
  return clientConfig().then((config) => config.mapsBrowserKey);
}

export async function loadMaps(): Promise<typeof google.maps | null> {
  apiPromise ??= injectScript();
  return apiPromise;
}

async function injectScript(): Promise<typeof google.maps | null> {
  const key = await mapsBrowserKey();
  if (!key) return null;
  if (window.google?.maps) return window.google.maps;

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    const params = new URLSearchParams({
      key,
      language: 'fr',
      region: 'FR',
      loading: 'async',
      // `geometry` decodes the encoded polylines the server brings back from the Routes API.
      libraries: 'geometry',
      callback: '__ergoMapsReady',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;

    const settle = (value: typeof google.maps | null) => {
      delete (window as Window & { __ergoMapsReady?: () => void }).__ergoMapsReady;
      resolve(value);
    };

    (window as Window & { __ergoMapsReady?: () => void }).__ergoMapsReady = () =>
      settle(window.google?.maps ?? null);
    script.onerror = () => {
      // A blocked or failed script must not retry on every render, so the
      // rejected promise stays cached — the map is simply absent this session.
      settle(null);
    };

    document.head.append(script);
  });
}

/**
 * The browser key, or null while it is being fetched and forever after when
 * there is none. Screens use it to decide between the map and a sober hint.
 */
export function useMapsKey(): { key: string | null; resolved: boolean } {
  const [state, setState] = useState<{ key: string | null; resolved: boolean }>({
    key: null,
    resolved: false,
  });

  useEffect(() => {
    let active = true;
    void mapsBrowserKey().then((key) => {
      if (active) setState({ key, resolved: true });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
}

/**
 * Whether the server can send an email at all. Resolved rather than assumed, so
 * « Envoyer par email » shows why it is disabled instead of failing on click.
 */
export function useEmailConfigured(): { configured: boolean; resolved: boolean } {
  const [state, setState] = useState({ configured: false, resolved: false });

  useEffect(() => {
    let active = true;
    void clientConfig().then((config) => {
      if (active) setState({ configured: config.emailConfigured, resolved: true });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
}

export interface MapPoint {
  lat: number;
  lng: number;
  label?: string;
  title?: string;
  /** A neighbouring place rather than one of the day's stops: grey, unnumbered. */
  muted?: boolean;
  /** « Maison »: a house pin joining the route, never a numbered stop. */
  home?: boolean;
  /** The séance being edited: the one pin the eye should land on first. */
  candidate?: boolean;
  /** The ride into this point as Google drew it; a straight segment without it. */
  encodedPolyline?: string | null;
}

/**
 * The streets of one hop, or null when there is nothing to draw — a leg without
 * geometry, or a Maps script whose `geometry` library never made it. Best-effort
 * like everything here: the caller falls back to a straight segment.
 */
export function decodePath(
  maps: typeof google.maps,
  encoded: string | null | undefined,
): google.maps.LatLng[] | null {
  if (!encoded) return null;
  try {
    const path = maps.geometry?.encoding?.decodePath(encoded);
    return path && path.length > 1 ? path : null;
  } catch {
    return null;
  }
}

/** Enough of a view to show every point, and never zoomed absurdly far in on one. */
const SINGLE_POINT_ZOOM = 15;

/**
 * `path` is the drawn route: a winding ride can leave the box its stops make, so
 * its own points count towards the view.
 */
export function fitToPoints(
  map: google.maps.Map,
  maps: typeof google.maps,
  points: MapPoint[],
  path: (google.maps.LatLng | google.maps.LatLngLiteral)[] = [],
): void {
  if (points.length === 0) return;
  if (points.length === 1 && path.length === 0) {
    const only = points[0] as MapPoint;
    map.setCenter({ lat: only.lat, lng: only.lng });
    map.setZoom(SINGLE_POINT_ZOOM);
    return;
  }

  const bounds = new maps.LatLngBounds();
  for (const point of points) bounds.extend({ lat: point.lat, lng: point.lng });
  for (const point of path) bounds.extend(point);
  map.fitBounds(bounds, 32);
}

export const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'cooperative',
  clickableIcons: false,
};
